import * as React from "react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: string;
  /** Optional leading icon or glyph shown before the label. */
  icon?: React.ReactNode;
  /** Shown but not choosable. Rendered with `aria-disabled` rather than the
   *  `disabled` attribute so its `title` still explains why on hover. */
  disabled?: boolean;
  /** Native tooltip — typically the reason a disabled option is unavailable. */
  title?: string;
  /** Styled hover tooltip explaining the option. Replaces `title` when set,
   *  so the two never show at once. */
  tooltip?: React.ReactNode;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: SegmentedControlOption<T>[];
  className?: string;
  /** Disables every option, e.g. while the surrounding form is submitting. */
  disabled?: boolean;
  /** Accessible name for the group. */
  "aria-label"?: string;
  /**
   * `boxed` (default): one bordered pill split by dividers, the selected
   * segment filled. `bare`: no frame and no dividers — only the selected
   * option is marked, by a border (the Gantt's toolbar).
   */
  variant?: "boxed" | "bare";
  /** `bare` only: the selected option's border — `neutral` grey (default) or
   *  `brand` red. */
  tone?: "neutral" | "brand";
}

/**
 * SegmentedControl — a row of mutually-exclusive options shown side by side,
 * with the active option highlighted. Matches `Button size="control"` (h-9,
 * px-3, rounded-md, 11px, font-medium, border-input) so it lines up with the
 * adjacent toolbar controls. The selected segment uses the app's standard
 * selected-state treatment: a muted fill with foreground text, while inactive
 * segments stay muted-foreground until hovered.
 */
export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  options,
  className,
  disabled = false,
  "aria-label": ariaLabel,
  variant = "boxed",
  tone = "neutral",
}: SegmentedControlProps<T>) {
  const bare = variant === "bare";
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        bare
          ? "inline-flex h-9 items-stretch gap-0.5"
          : "inline-flex h-9 items-stretch overflow-hidden rounded-md border border-input bg-background",
        className
      )}
    >
      {options.map((option, i) => {
        const isActive = option.value === value;
        const isDisabled = disabled || option.disabled === true;
        const button = (
          <button
            key={option.value}
            type="button"
            aria-pressed={isActive}
            aria-disabled={isDisabled || undefined}
            title={option.tooltip == null ? option.title : undefined}
            onClick={() => {
              if (!isDisabled) onValueChange(option.value);
            }}
            className={cn(
              "flex items-center gap-2 px-3 text-[11px] font-medium transition-colors [&_svg]:size-3.5 [&_svg]:shrink-0",
              bare
                ? cn(
                    "rounded-md border",
                    isActive
                      ? cn(
                          "text-foreground",
                          tone === "brand" ? "border-[#ef4129]" : "border-input"
                        )
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  )
                : cn(
                    i > 0 && "border-l border-input",
                    isActive
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                  ),
              isDisabled && "cursor-not-allowed opacity-50 hover:bg-transparent"
            )}
          >
            {option.icon != null && (
              <span aria-hidden="true" className="inline-flex items-center">
                {option.icon}
              </span>
            )}
            <span>{option.label}</span>
          </button>
        );
        if (option.tooltip == null) return button;
        return (
          <TooltipProvider key={option.value}>
            <Tooltip>
              <TooltipTrigger asChild>{button}</TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-xs">
                {option.tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
}
